import { useEffect, useState, type FormEvent } from 'react';
import type {
  InflowFrequency,
  SolTemporalKpiSnapshot,
  WindfallAllocationMode,
} from '@economi/domain-contracts';
import {
  addDaysToIso,
  bpToPercent,
  centsToSoles,
  computeWindfallContributionCents,
  formatSolesFromCents,
  getDefaultOccurrencesForFrequency,
  getRecurringExpenseAmountForMonthCents,
  INFLOW_FREQUENCY_LABELS,
  MAX_USER_CREDIT_CARDS,
  percentToBp,
  resolveCardBillingCloseDateForCycle,
  resolveCardPaymentDueDateForCycle,
  solesToCents,
  type CreditCardProfileDto,
  type FinancialCycleState,
  type RecurringInflowItem,
  type WindfallInflowItem,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './CycleAndInflowsView.module.css';

interface CycleAndInflowsViewProps {
  readonly cycleState: FinancialCycleState;
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly onUpdateCyclePolicy: (params: {
    spendingRatePercent: number;
    keepRateConstantAcrossPeriods: boolean;
    startDateIso?: string;
    renewalDateIso: string;
  }) => void;
  readonly onRenewCycleNow?: () => void;
  readonly onUpsertCreditCard?: (params: {
    id?: string;
    alias: string;
    issuerBank: string;
    lastFourDigits: string;
    colorHex: string;
    billingCloseDayOfMonth: number;
    paymentDueDayOfMonth: number;
    monthlyLimitSoles: number | null;
  }) => void;
  readonly onUpdateCreditCard?: (
    cardId: string,
    params: {
      alias: string;
      issuerBank: string;
      lastFourDigits: string;
      colorHex: string;
      billingCloseDayOfMonth: number;
      paymentDueDayOfMonth: number;
      monthlyLimitSoles: number | null;
    }
  ) => void;
  readonly onDeleteCreditCard?: (cardId: string) => void;
  readonly onAddRecurringInflow: (params: {
    name: string;
    amountSoles: number;
    frequency: InflowFrequency;
    occurrencesPerCycle: number;
    customIntervalDays?: number;
    renewalDayOfMonth: number;
    secondBillingDayOfMonth?: number;
    autoUpdateCycleRenewalDate?: boolean;
  }) => void;
  readonly onUpdateRecurringInflow: (
    ruleId: string,
    params: {
      name: string;
      amountSoles: number;
      frequency: InflowFrequency;
      occurrencesPerCycle: number;
      customIntervalDays?: number;
      renewalDayOfMonth: number;
      secondBillingDayOfMonth?: number;
    }
  ) => void;
  readonly onDeleteRecurringInflow: (ruleId: string) => void;
  readonly onAddWindfallInflow: (params: {
    description: string;
    amountSoles: number;
    allocationMode: WindfallAllocationMode;
    customAllocationPercent: number;
    receivedAtIso?: string;
  }) => void;
  readonly onUpdateWindfallInflow: (
    inflowId: string,
    params: {
      description: string;
      amountSoles: number;
      allocationMode: WindfallAllocationMode;
      customAllocationPercent: number;
      receivedAtIso?: string;
    }
  ) => void;
  readonly onDeleteWindfallInflow: (inflowId: string) => void;
  readonly onOpenRecurringExpensesView?: () => void;
}

const ALLOCATION_LABELS: Record<WindfallAllocationMode, string> = {
  USE_CYCLE_RATE: 'Aplicar % del ciclo actual',
  CUSTOM_RATE: 'Porcentaje personalizado para este ingreso',
  RESERVE_ONLY: '100% Reserva / Ahorro (0% al límite de gasto)',
};

export function CycleAndInflowsView({
  cycleState,
  snapshot,
  onUpdateCyclePolicy,
  onRenewCycleNow,
  onUpsertCreditCard,
  onUpdateCreditCard,
  onDeleteCreditCard,
  onAddRecurringInflow,
  onUpdateRecurringInflow,
  onDeleteRecurringInflow,
  onAddWindfallInflow,
  onUpdateWindfallInflow,
  onDeleteWindfallInflow,
  onOpenRecurringExpensesView,
}: CycleAndInflowsViewProps) {
  const [ratePercent, setRatePercent] = useState<number>(
    bpToPercent(cycleState.spendingRateBp)
  );
  const [keepConstant, setKeepConstant] = useState<boolean>(
    cycleState.keepRateConstantAcrossPeriods
  );
  const [startDateIso, setStartDateIso] = useState<string>(
    cycleState.startDateIso
  );
  const [renewalDateIso, setRenewalDateIso] = useState<string>(
    cycleState.renewalDateIso
  );

  useEffect(() => {
    setRatePercent(bpToPercent(cycleState.spendingRateBp));
    setKeepConstant(cycleState.keepRateConstantAcrossPeriods);
    setStartDateIso(cycleState.startDateIso);
    setRenewalDateIso(cycleState.renewalDateIso);
  }, [
    cycleState.spendingRateBp,
    cycleState.keepRateConstantAcrossPeriods,
    cycleState.startDateIso,
    cycleState.renewalDateIso,
  ]);

  const activeMonthIso = cycleState.startDateIso.slice(0, 7);
  const activeRecurringRules = (cycleState.recurringExpenses ?? []).filter(
    (r) => r.isActive !== false
  );
  const autoDeductedRecurringCents = activeRecurringRules.reduce(
    (acc, r) => acc + getRecurringExpenseAmountForMonthCents(r, activeMonthIso),
    0
  );

  // Estado formulario Nuevo Ingreso Recurrente
  const [recName, setRecName] = useState<string>('');
  const [recAmountSoles, setRecAmountSoles] = useState<string>('');
  const [recFrequency, setRecFrequency] = useState<InflowFrequency>('MONTHLY');
  const [recOccurrences, setRecOccurrences] = useState<number>(1);
  const [recCustomDays, setRecCustomDays] = useState<number>(14);
  const [recDay, setRecDay] = useState<number>(30);
  const [recSecondDay, setRecSecondDay] = useState<number>(15);
  const [autoUpdateRenewal, setAutoUpdateRenewal] = useState<boolean>(false);

  // Estado de edición en línea de Ingreso Recurrente existente
  const [editingRecId, setEditingRecId] = useState<string | null>(null);
  const [editRecName, setEditRecName] = useState<string>('');
  const [editRecAmountSoles, setEditRecAmountSoles] = useState<string>('');
  const [editRecFrequency, setEditRecFrequency] =
    useState<InflowFrequency>('MONTHLY');
  const [editRecOccurrences, setEditRecOccurrences] = useState<number>(1);
  const [editRecCustomDays, setEditRecCustomDays] = useState<number>(14);
  const [editRecDay, setEditRecDay] = useState<number>(30);
  const [editRecSecondDay, setEditRecSecondDay] = useState<number>(15);

  // Estado formulario Nuevo Ingreso No Recurrente (Windfall)
  const [wfDesc, setWfDesc] = useState<string>('');
  const [wfAmountSoles, setWfAmountSoles] = useState<string>('');
  const [wfMode, setWfMode] = useState<WindfallAllocationMode>('USE_CYCLE_RATE');
  const [wfCustomPercent, setWfCustomPercent] = useState<number>(50);
  const [wfDateIso, setWfDateIso] = useState<string>(
    cycleState.referenceDateIso
  );

  // Estado de edición en línea de Ingreso Extraordinario existente
  const [editingWfId, setEditingWfId] = useState<string | null>(null);
  const [editWfDesc, setEditWfDesc] = useState<string>('');
  const [editWfAmountSoles, setEditWfAmountSoles] = useState<string>('');
  const [editWfMode, setEditWfMode] =
    useState<WindfallAllocationMode>('USE_CYCLE_RATE');
  const [editWfCustomPercent, setEditWfCustomPercent] = useState<number>(50);
  const [editWfDateIso, setEditWfDateIso] = useState<string>(
    cycleState.referenceDateIso
  );

  // Estado formulario Nueva Tarjeta de Crédito (Máximo 3 Tarjetas)
  const [cardAlias, setCardAlias] = useState<string>('');
  const [cardBank, setCardBank] = useState<string>('BCP');
  const [cardLastFour, setCardLastFour] = useState<string>('');
  const [cardColor, setCardColor] = useState<string>('#10b981');
  const [cardCloseDay, setCardCloseDay] = useState<number>(20);
  const [cardDueDay, setCardDueDay] = useState<number>(5);
  const [cardMonthlyLimitSoles, setCardMonthlyLimitSoles] =
    useState<string>('');

  // Estado de edición en línea de Tarjeta de Crédito existente
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editCardAlias, setEditCardAlias] = useState<string>('');
  const [editCardBank, setEditCardBank] = useState<string>('');
  const [editCardLastFour, setEditCardLastFour] = useState<string>('');
  const [editCardColor, setEditCardColor] = useState<string>('#10b981');
  const [editCardCloseDay, setEditCardCloseDay] = useState<number>(20);
  const [editCardDueDay, setEditCardDueDay] = useState<number>(5);
  const [editCardLimitSoles, setEditCardLimitSoles] = useState<string>('');

  const configuredCards = cycleState.creditCards ?? [];
  const canAddMoreCards = configuredCards.length < MAX_USER_CREDIT_CARDS;

  const previewNewCardCloseDateIso = resolveCardBillingCloseDateForCycle(
    cardCloseDay,
    startDateIso,
    renewalDateIso
  );
  const previewNewCardDueDateIso = resolveCardPaymentDueDateForCycle(
    cardDueDay,
    previewNewCardCloseDateIso
  );

  const handleCardCreateSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!onUpsertCreditCard || !canAddMoreCards) return;
    const cleanAlias = cardAlias.trim() || `Tarjeta ${cardBank.trim() || 'Principal'}`;
    const parsedLimit = Number(cardMonthlyLimitSoles);
    const monthlyLimitSoles =
      cardMonthlyLimitSoles.trim().length > 0 && parsedLimit > 0
        ? parsedLimit
        : null;

    onUpsertCreditCard({
      alias: cleanAlias,
      issuerBank: cardBank.trim(),
      lastFourDigits: cardLastFour.replace(/\D/g, '').slice(0, 4),
      colorHex: cardColor || '#10b981',
      billingCloseDayOfMonth: Math.min(31, Math.max(1, Number(cardCloseDay) || 20)),
      paymentDueDayOfMonth: Math.min(31, Math.max(1, Number(cardDueDay) || 5)),
      monthlyLimitSoles,
    });

    setCardAlias('');
    setCardLastFour('');
    setCardMonthlyLimitSoles('');
  };

  const startEditingCreditCard = (card: CreditCardProfileDto) => {
    setEditingCardId(card.id);
    setEditCardAlias(card.alias);
    setEditCardBank(card.issuerBank ?? '');
    setEditCardLastFour(card.lastFourDigits ?? '');
    setEditCardColor(card.colorHex ?? '#10b981');
    setEditCardCloseDay(card.billingCloseDayOfMonth);
    setEditCardDueDay(card.paymentDueDayOfMonth);
    setEditCardLimitSoles(
      typeof card.monthlyLimitCents === 'number' && card.monthlyLimitCents > 0
        ? String(centsToSoles(card.monthlyLimitCents))
        : ''
    );
  };

  const handleSaveEditCreditCard = (cardId: string) => {
    if (!onUpdateCreditCard) return;
    const parsedLimit = Number(editCardLimitSoles);
    const monthlyLimitSoles =
      editCardLimitSoles.trim().length > 0 && parsedLimit > 0
        ? parsedLimit
        : null;

    onUpdateCreditCard(cardId, {
      alias: editCardAlias.trim() || 'Tarjeta de Crédito',
      issuerBank: editCardBank.trim(),
      lastFourDigits: editCardLastFour.replace(/\D/g, '').slice(0, 4),
      colorHex: editCardColor || '#10b981',
      billingCloseDayOfMonth: Math.min(
        31,
        Math.max(1, Number(editCardCloseDay) || 20)
      ),
      paymentDueDayOfMonth: Math.min(
        31,
        Math.max(1, Number(editCardDueDay) || 5)
      ),
      monthlyLimitSoles,
    });
    setEditingCardId(null);
  };

  const handlePolicyChange = (
    nextRate: number,
    nextKeepConstant: boolean,
    nextStartIso: string,
    nextRenewalIso: string
  ) => {
    setRatePercent(nextRate);
    setKeepConstant(nextKeepConstant);
    setStartDateIso(nextStartIso);
    setRenewalDateIso(nextRenewalIso);
    onUpdateCyclePolicy({
      spendingRatePercent: nextRate,
      keepRateConstantAcrossPeriods: nextKeepConstant,
      startDateIso: nextStartIso,
      renewalDateIso: nextRenewalIso,
    });
  };

  const handleFrequencyChange = (nextFreq: InflowFrequency) => {
    setRecFrequency(nextFreq);
    const defaultOcc = getDefaultOccurrencesForFrequency(nextFreq);
    setRecOccurrences(defaultOcc);
    if (nextFreq === 'BIWEEKLY') {
      setRecSecondDay(15);
      setRecDay(30);
    }
  };

  const unitRecCents = solesToCents(Math.max(0, Number(recAmountSoles) || 0));
  const totalRecCycleCents = unitRecCents * Math.max(1, recOccurrences);

  const handleRecurringSubmit = (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(recAmountSoles) || 0;
    if (amount <= 0) return;

    onAddRecurringInflow({
      name: recName.trim() || 'Ingreso Recurrente',
      amountSoles: amount,
      frequency: recFrequency,
      occurrencesPerCycle: Math.max(1, recOccurrences),
      customIntervalDays:
        recFrequency === 'CUSTOM_DAYS' ? Math.max(1, recCustomDays) : undefined,
      renewalDayOfMonth: recDay,
      secondBillingDayOfMonth:
        recFrequency === 'BIWEEKLY' ? recSecondDay : undefined,
      autoUpdateCycleRenewalDate: autoUpdateRenewal,
    });

    if (autoUpdateRenewal) {
      const daysToAdd =
        recFrequency === 'WEEKLY'
          ? 7 * Math.max(1, recOccurrences)
          : recFrequency === 'BIWEEKLY'
            ? 15 * Math.max(1, recOccurrences)
            : recFrequency === 'CUSTOM_DAYS'
              ? Math.max(1, recCustomDays) * Math.max(1, recOccurrences)
              : 30;
      const computedNextRenewal = addDaysToIso(startDateIso, daysToAdd);
      setRenewalDateIso(computedNextRenewal);
    }

    setRecName('');
    setRecAmountSoles('');
  };

  const startEditingRecurring = (rec: RecurringInflowItem) => {
    setEditingRecId(rec.id);
    setEditRecName(rec.name);
    const occ = rec.occurrencesPerCycle ?? 1;
    const perOccCents =
      rec.amountPerOccurrenceCents ?? Math.round(rec.amountCents / Math.max(1, occ));
    setEditRecAmountSoles(String(centsToSoles(perOccCents)));
    setEditRecFrequency(rec.frequency);
    setEditRecOccurrences(occ);
    setEditRecCustomDays(rec.customIntervalDays ?? 14);
    setEditRecDay(rec.renewalDayOfMonth);
    setEditRecSecondDay(rec.secondBillingDayOfMonth ?? 15);
  };

  const handleSaveEditRecurring = (ruleId: string) => {
    const amount = Number(editRecAmountSoles) || 0;
    if (amount <= 0) return;
    onUpdateRecurringInflow(ruleId, {
      name: editRecName.trim() || 'Ingreso Recurrente',
      amountSoles: amount,
      frequency: editRecFrequency,
      occurrencesPerCycle: Math.max(1, editRecOccurrences),
      customIntervalDays:
        editRecFrequency === 'CUSTOM_DAYS'
          ? Math.max(1, editRecCustomDays)
          : undefined,
      renewalDayOfMonth: editRecDay,
      secondBillingDayOfMonth:
        editRecFrequency === 'BIWEEKLY' ? editRecSecondDay : undefined,
    });
    setEditingRecId(null);
  };

  const handleWindfallSubmit = (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(wfAmountSoles) || 0;
    if (amount <= 0) return;
    onAddWindfallInflow({
      description: wfDesc.trim() || 'Ingreso Extraordinario No Recurrente',
      amountSoles: amount,
      allocationMode: wfMode,
      customAllocationPercent: wfCustomPercent,
      receivedAtIso: wfDateIso,
    });
    setWfDesc('');
    setWfAmountSoles('');
  };

  const startEditingWindfall = (wf: WindfallInflowItem) => {
    setEditingWfId(wf.id);
    setEditWfDesc(wf.description);
    setEditWfAmountSoles(String(centsToSoles(wf.amountCents)));
    setEditWfMode(wf.allocationMode);
    setEditWfCustomPercent(bpToPercent(wf.customAllocationBp));
    setEditWfDateIso(wf.receivedAtIso.slice(0, 10));
  };

  const handleSaveEditWindfall = (inflowId: string) => {
    const amount = Number(editWfAmountSoles) || 0;
    if (amount <= 0) return;
    onUpdateWindfallInflow(inflowId, {
      description: editWfDesc.trim() || 'Ingreso Extraordinario',
      amountSoles: amount,
      allocationMode: editWfMode,
      customAllocationPercent: editWfCustomPercent,
      receivedAtIso: editWfDateIso,
    });
    setEditingWfId(null);
  };

  const previewWfCents = solesToCents(Math.max(0, Number(wfAmountSoles) || 0));
  const previewWfSplit = computeWindfallContributionCents(
    previewWfCents,
    wfMode,
    percentToBp(ratePercent),
    percentToBp(wfCustomPercent)
  );

  return (
    <div className={styles.container}>
      {/* Barra superior informativa con acceso directo a la vista separada de Gastos Recurrentes */}
      <div className={styles.topBannerBar}>
        <div className={styles.topBannerText}>
          <span
            className={styles.topBannerTitle}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
          >
            💰 Ingresos, Ciclo y Tarjetas
            <InfoPopover title="Configuración del Ciclo y Tarjetas">
              Define tu fecha de recepción de ingresos, tu tasa de gasto permitido y hasta 3 tarjetas de crédito con sus fechas de facturación y pago.
            </InfoPopover>
          </span>
        </div>
        {onOpenRecurringExpensesView && (
          <button
            type="button"
            className={styles.switchViewBtn}
            onClick={onOpenRecurringExpensesView}
          >
            🔄 Ir a Gastos Recurrentes →
          </button>
        )}
      </div>

      <div className={styles.topGrid}>
        {/* Panel 1: Política de Porcentaje de Límite de Gasto y Fechas del Ciclo */}
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h2 className={styles.title}>1. Política de Límite</h2>
            <InfoPopover title="Política de Límite y Fechas">
              Define qué porcentaje de tus ingresos conforma tu Tope de Gasto (el 100% permitido) y las fechas de inicio y renovación de tu ciclo.
            </InfoPopover>
          </div>

          <div className={styles.policyBox}>
            <div className={styles.rateHeader}>
              <div>
                <span className={styles.label}>LÍMITE SOBRE INGRESOS</span>
                <p className={styles.itemMeta}>
                  Reserva protegida: {(100 - ratePercent).toFixed(0)}% (
                  {formatSolesFromCents(snapshot.protectedSavingsCents)})
                </p>
              </div>
              <span className={styles.rateBig}>{ratePercent}%</span>
            </div>

            <input
              type="range"
              min={1}
              max={100}
              step={1}
              value={ratePercent}
              onChange={(e) =>
                handlePolicyChange(
                  Number(e.target.value),
                  keepConstant,
                  startDateIso,
                  renewalDateIso
                )
              }
              className={styles.slider}
              aria-label="Porcentaje del límite de gasto"
            />

            <div className={styles.modeToggleGroup}>
              <button
                type="button"
                className={`${styles.modeBtn} ${keepConstant ? styles.modeBtnActive : ''}`}
                onClick={() =>
                  handlePolicyChange(ratePercent, true, startDateIso, renewalDateIso)
                }
              >
                🔒 {ratePercent}% constante
              </button>
              <button
                type="button"
                className={`${styles.modeBtn} ${!keepConstant ? styles.modeBtnActive : ''}`}
                onClick={() =>
                  handlePolicyChange(ratePercent, false, startDateIso, renewalDateIso)
                }
              >
                🎛️ Solo este período
              </button>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="cycle-start-date" className={styles.label}>
                  Inicio del Ciclo
                </label>
                <input
                  id="cycle-start-date"
                  type="date"
                  value={startDateIso}
                  onChange={(e) =>
                    handlePolicyChange(
                      ratePercent,
                      keepConstant,
                      e.target.value,
                      renewalDateIso
                    )
                  }
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="cycle-renewal-date" className={styles.label}>
                  Fecha de Renovación
                </label>
                <input
                  id="cycle-renewal-date"
                  type="date"
                  value={renewalDateIso}
                  onChange={(e) =>
                    handlePolicyChange(
                      ratePercent,
                      keepConstant,
                      startDateIso,
                      e.target.value
                    )
                  }
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <span className={styles.label}>
                Tope Efectivo ({snapshot.totalCycleDays}d · Faltan {snapshot.remainingDays}d)
              </span>
              <div
                className={styles.input}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 700,
                  color: 'var(--accent-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>{formatSolesFromCents(snapshot.effectiveSpendingCapCents)}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  Cuota Segura: {formatSolesFromCents(snapshot.dynamicSafeDailyAllowanceCents)}/día
                </span>
              </div>
            </div>

            <div className={styles.frequencyPreviewBox}>
              <div className={styles.frequencyCalcRow}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  🔒 Descuento Recurrentes ({cycleState.startDateIso.slice(0, 10)})
                  <InfoPopover title="Descuento Automático en Día 1">
                    Todos tus gastos recurrentes activos se descuentan de inmediato el Día 1 de cada ciclo ({cycleState.startDateIso.slice(0, 10)}), dejando libre tu saldo real: {formatSolesFromCents(snapshot.remainingCapCents)}.
                  </InfoPopover>
                </span>
                <strong
                  style={{
                    color: '#fbbf24',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  -{formatSolesFromCents(autoDeductedRecurringCents)} (
                  {activeRecurringRules.length} fijos)
                </strong>
              </div>
              {onRenewCycleNow && (
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  style={{ width: '100%', justifyContent: 'center', marginTop: '0.25rem' }}
                  onClick={onRenewCycleNow}
                >
                  🔄 Renovar Ciclo Ahora
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Panel 2: Gestión de Ingresos Recurrentes con Frecuencia Completa */}
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h2 className={styles.title}>
              2. Mis Ingresos ({cycleState.recurringInflows.length})
            </h2>
            <InfoPopover title="Ingresos Recurrentes y Frecuencia">
              Configura tu sueldo mensual, quincenal, semanal o cada N días. Puedes editarlo o eliminarlo en cualquier momento.
            </InfoPopover>
          </div>

          <form onSubmit={handleRecurringSubmit} className={styles.formStack}>
            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="rec-name" className={styles.label}>
                  Concepto de Ingreso Recurrente
                </label>
                <input
                  id="rec-name"
                  type="text"
                  placeholder="Ej. Sueldo Planilla, Quincena, Honorarios"
                  value={recName}
                  onChange={(e) => setRecName(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="rec-amount" className={styles.label}>
                  Monto por Abono (S/)
                </label>
                <input
                  id="rec-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Ej. 2500.00"
                  value={recAmountSoles}
                  onChange={(e) => setRecAmountSoles(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.fieldRowThree}>
              <div className={styles.fieldGroup}>
                <label htmlFor="rec-freq" className={styles.label}>
                  Frecuencia de Abono
                </label>
                <select
                  id="rec-freq"
                  value={recFrequency}
                  onChange={(e) =>
                    handleFrequencyChange(e.target.value as InflowFrequency)
                  }
                  className={styles.select}
                >
                  <option value="MONTHLY">Mensual (1 vez al mes)</option>
                  <option value="BIWEEKLY">Quincenal (Cada 15 días)</option>
                  <option value="WEEKLY">Semanal (Cada 7 días)</option>
                  <option value="CUSTOM_DAYS">Cada N días (Personalizado)</option>
                </select>
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="rec-occurrences" className={styles.label}>
                  Abonos en este Ciclo
                </label>
                <select
                  id="rec-occurrences"
                  value={recOccurrences}
                  onChange={(e) => setRecOccurrences(Number(e.target.value) || 1)}
                  className={styles.select}
                >
                  <option value={1}>1 abono en el ciclo (×1)</option>
                  <option value={2}>2 abonos en el ciclo (×2)</option>
                  <option value={3}>3 abonos en el ciclo (×3)</option>
                  <option value={4}>4 abonos en el ciclo (×4)</option>
                  <option value={5}>5 abonos en el ciclo (×5)</option>
                </select>
              </div>

              {recFrequency === 'CUSTOM_DAYS' ? (
                <div className={styles.fieldGroup}>
                  <label htmlFor="rec-custom-days" className={styles.label}>
                    Cada cuántos días
                  </label>
                  <input
                    id="rec-custom-days"
                    type="number"
                    min={1}
                    max={365}
                    value={recCustomDays}
                    onChange={(e) => setRecCustomDays(Number(e.target.value) || 14)}
                    className={styles.input}
                  />
                </div>
              ) : recFrequency === 'BIWEEKLY' ? (
                <div className={styles.fieldGroup}>
                  <label htmlFor="rec-second-day" className={styles.label}>
                    Días Quincena (1º y 2º)
                  </label>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <input
                      id="rec-second-day"
                      type="number"
                      min={1}
                      max={31}
                      value={recSecondDay}
                      onChange={(e) =>
                        setRecSecondDay(Number(e.target.value) || 15)
                      }
                      className={styles.input}
                      title="Día 1er abono quincenal"
                    />
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={recDay}
                      onChange={(e) => setRecDay(Number(e.target.value) || 30)}
                      className={styles.input}
                      title="Día 2do abono quincenal"
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.fieldGroup}>
                  <label htmlFor="rec-day" className={styles.label}>
                    Día de Abono (1–31)
                  </label>
                  <input
                    id="rec-day"
                    type="number"
                    min={1}
                    max={31}
                    value={recDay}
                    onChange={(e) => setRecDay(Number(e.target.value) || 30)}
                    className={styles.input}
                  />
                </div>
              )}
            </div>

            <div className={styles.frequencyPreviewBox}>
              <div className={styles.frequencyCalcRow}>
                <span>
                  Cálculo por frecuencia ({INFLOW_FREQUENCY_LABELS[recFrequency]}):
                </span>
                <strong style={{ color: 'var(--accent-primary)', fontFamily: 'var(--font-mono)' }}>
                  {formatSolesFromCents(unitRecCents)} × {recOccurrences} abono(s) = +
                  {formatSolesFromCents(totalRecCycleCents)} en el ciclo
                </strong>
              </div>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={autoUpdateRenewal}
                  onChange={(e) => setAutoUpdateRenewal(e.target.checked)}
                />
                <span>
                  Ajustar automáticamente la Fecha de Renovación del ciclo según esta frecuencia
                </span>
              </label>
            </div>

            <button type="submit" className={styles.primaryBtn}>
              + Registrar Ingreso Recurrente
            </button>
          </form>

          <div className={styles.list}>
            {cycleState.recurringInflows.length === 0 ? (
              <div className={styles.emptyStateBox}>
                Aún no has registrado ingresos recurrentes. Ingresa tu sueldo o ingreso periódico arriba para calcular tu capacidad de gasto.
              </div>
            ) : (
              cycleState.recurringInflows.map((rec) => {
                const isEditing = editingRecId === rec.id;
                const occ = rec.occurrencesPerCycle ?? 1;
                const unitCents =
                  rec.amountPerOccurrenceCents ??
                  Math.round(rec.amountCents / Math.max(1, occ));

                return (
                  <div key={rec.id} className={styles.listItem}>
                    <div className={styles.listItemMain}>
                      <div>
                        <div className={styles.itemTitle}>{rec.name}</div>
                        <div className={styles.itemMeta}>
                          {INFLOW_FREQUENCY_LABELS[rec.frequency]}
                          {occ > 1
                            ? ` · ${formatSolesFromCents(unitCents)} × ${occ} abonos`
                            : ''}{' '}
                          · Día de abono:{' '}
                          {rec.frequency === 'BIWEEKLY' && rec.secondBillingDayOfMonth
                            ? `${rec.secondBillingDayOfMonth} y ${rec.renewalDayOfMonth}`
                            : rec.renewalDayOfMonth}
                        </div>
                        <div className={styles.itemActionsRow}>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() =>
                              isEditing
                                ? setEditingRecId(null)
                                : startEditingRecurring(rec)
                            }
                          >
                            {isEditing ? '✕ Cerrar Edición' : '✏️ Editar'}
                          </button>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            onClick={() => onDeleteRecurringInflow(rec.id)}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      </div>

                      <span className={styles.itemAmount}>
                        +{formatSolesFromCents(rec.amountCents)}
                      </span>
                    </div>

                    {isEditing && (
                      <div className={styles.inlineEditBox}>
                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Concepto</label>
                            <input
                              type="text"
                              value={editRecName}
                              onChange={(e) => setEditRecName(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Monto por Abono (S/)
                            </label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={editRecAmountSoles}
                              onChange={(e) => setEditRecAmountSoles(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.fieldRowThree}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Frecuencia</label>
                            <select
                              value={editRecFrequency}
                              onChange={(e) => {
                                const nextF = e.target.value as InflowFrequency;
                                setEditRecFrequency(nextF);
                                setEditRecOccurrences(
                                  getDefaultOccurrencesForFrequency(nextF)
                                );
                              }}
                              className={styles.select}
                            >
                              <option value="MONTHLY">Mensual</option>
                              <option value="BIWEEKLY">Quincenal</option>
                              <option value="WEEKLY">Semanal</option>
                              <option value="CUSTOM_DAYS">Cada N días</option>
                            </select>
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Abonos en Ciclo</label>
                            <input
                              type="number"
                              min={1}
                              max={31}
                              value={editRecOccurrences}
                              onChange={(e) =>
                                setEditRecOccurrences(Number(e.target.value) || 1)
                              }
                              className={styles.input}
                            />
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Día Abono (1–31)</label>
                            <input
                              type="number"
                              min={1}
                              max={31}
                              value={editRecDay}
                              onChange={(e) =>
                                setEditRecDay(Number(e.target.value) || 30)
                              }
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.itemActionsRow}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => handleSaveEditRecurring(rec.id)}
                          >
                            💾 Guardar Cambios
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => setEditingRecId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Sección: Mis Tarjetas de Crédito (Máximo 3 Tarjetas) */}
      <div className={styles.windfallSection}>
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h3 className={styles.title}>
              💳 Mis Tarjetas de Crédito (Máx. {MAX_USER_CREDIT_CARDS})
            </h3>
            <InfoPopover title="Tarjetas de Crédito (Máx. 3)">
              Registra el Día de Facturación y Día de Pago de tus tarjetas para controlar hasta qué fecha puedes gastar en el ciclo actual y qué compras pasan automáticamente al Próximo Ciclo.
            </InfoPopover>
          </div>

          {!canAddMoreCards ? (
            <div className={styles.frequencyPreviewBox}>
              <strong>
                ✅ Límite alcanzado ({MAX_USER_CREDIT_CARDS}/{MAX_USER_CREDIT_CARDS} tarjetas)
              </strong>
            </div>
          ) : (
            <form onSubmit={handleCardCreateSubmit} className={styles.formStack}>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label htmlFor="card-alias" className={styles.label}>
                    Alias de Tarjeta *
                  </label>
                  <input
                    id="card-alias"
                    type="text"
                    placeholder="Ej. Visa Signature BCP, Amex Interbank"
                    value={cardAlias}
                    onChange={(e) => setCardAlias(e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label htmlFor="card-bank" className={styles.label}>
                    Banco Emisor
                  </label>
                  <input
                    id="card-bank"
                    type="text"
                    placeholder="Ej. BCP, Interbank, BBVA, Scotiabank"
                    value={cardBank}
                    onChange={(e) => setCardBank(e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.fieldRowThree}>
                <div className={styles.fieldGroup}>
                  <label
                    htmlFor="card-close-day"
                    className={styles.label}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    Día de Facturación (1–31) *
                    <InfoPopover title="Día de Facturación">
                      Fecha de corte de tu tarjeta: indica hasta qué día tus compras entran en el estado de cuenta del ciclo actual.
                    </InfoPopover>
                  </label>
                  <input
                    id="card-close-day"
                    type="number"
                    min={1}
                    max={31}
                    value={cardCloseDay}
                    onChange={(e) =>
                      setCardCloseDay(
                        Math.min(31, Math.max(1, Number(e.target.value) || 20))
                      )
                    }
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label
                    htmlFor="card-due-day"
                    className={styles.label}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    Día de Pago (1–31) *
                    <InfoPopover title="Día de Pago">
                      Fecha límite de pago del estado de cuenta de tu tarjeta en el ciclo.
                    </InfoPopover>
                  </label>
                  <input
                    id="card-due-day"
                    type="number"
                    min={1}
                    max={31}
                    value={cardDueDay}
                    onChange={(e) =>
                      setCardDueDay(
                        Math.min(31, Math.max(1, Number(e.target.value) || 5))
                      )
                    }
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label htmlFor="card-limit" className={styles.label}>
                    Tope Mensual (S/)
                  </label>
                  <input
                    id="card-limit"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Vacío = tope del ciclo"
                    value={cardMonthlyLimitSoles}
                    onChange={(e) => setCardMonthlyLimitSoles(e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label htmlFor="card-last4" className={styles.label}>
                    Últimos 4 dígitos
                  </label>
                  <input
                    id="card-last4"
                    type="text"
                    maxLength={4}
                    placeholder="Ej. 4821"
                    value={cardLastFour}
                    onChange={(e) =>
                      setCardLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))
                    }
                    className={styles.input}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label htmlFor="card-color" className={styles.label}>
                    Color
                  </label>
                  <select
                    id="card-color"
                    value={cardColor}
                    onChange={(e) => setCardColor(e.target.value)}
                    className={styles.select}
                  >
                    <option value="#10b981">🟢 Esmeralda (#10b981)</option>
                    <option value="#38bdf8">🔵 Celeste (#38bdf8)</option>
                    <option value="#f59e0b">🟠 Ámbar (#f59e0b)</option>
                    <option value="#a855f7">🟣 Violeta (#a855f7)</option>
                    <option value="#ec4899">🌸 Rosa (#ec4899)</option>
                  </select>
                </div>
              </div>

              <div className={styles.frequencyPreviewBox}>
                <div className={styles.frequencyCalcRow}>
                  <span>
                    📅 <strong>Gastar hasta: {previewNewCardCloseDateIso}</strong>
                  </span>
                  <span style={{ color: '#38bdf8', fontWeight: 700 }}>
                    🏦 Pago: {previewNewCardDueDateIso}
                  </span>
                </div>
              </div>

              <button type="submit" className={styles.primaryBtn}>
                + Guardar Tarjeta ({configuredCards.length + 1}/{MAX_USER_CREDIT_CARDS})
              </button>
            </form>
          )}
        </div>

        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h3 className={styles.title}>
              Tarjetas Configuradas ({configuredCards.length}/{MAX_USER_CREDIT_CARDS})
            </h3>
            <InfoPopover title="Corte y Pago en Ciclo">
              Fechas exactas de corte de facturación y pago calculadas para tu ciclo actual ({startDateIso.slice(0, 10)} → {renewalDateIso.slice(0, 10)}).
            </InfoPopover>
          </div>

          <div className={styles.list}>
            {configuredCards.length === 0 ? (
              <div className={styles.emptyStateBox}>
                Sin tarjetas registradas (máx. {MAX_USER_CREDIT_CARDS}).
              </div>
            ) : (
              configuredCards.map((card) => {
                const isEditingCard = editingCardId === card.id;
                const closeDateIso = resolveCardBillingCloseDateForCycle(
                  card.billingCloseDayOfMonth,
                  startDateIso,
                  renewalDateIso
                );
                const dueDateIso = resolveCardPaymentDueDateForCycle(
                  card.paymentDueDayOfMonth,
                  closeDateIso
                );
                const isOpenToday =
                  cycleState.referenceDateIso.slice(0, 10) <= closeDateIso;

                return (
                  <div
                    key={card.id}
                    className={styles.listItem}
                    style={{
                      borderLeft: `4px solid ${card.colorHex || '#10b981'}`,
                    }}
                  >
                    <div className={styles.listItemMain}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.28rem',
                        }}
                      >
                        <div className={styles.itemTitle}>
                          💳 {card.alias}{' '}
                          {card.issuerBank ? `(${card.issuerBank})` : ''}
                          {card.lastFourDigits
                            ? ` · •••• ${card.lastFourDigits}`
                            : ''}
                        </div>
                        <div>
                          <span className={styles.policyTag}>
                            {isOpenToday
                              ? '🟢 Abierta'
                              : '🔵 Cerrada (Próx. Ciclo)'}
                          </span>
                        </div>
                        <span className={styles.itemMeta}>
                          📅{' '}
                          <strong style={{ color: 'var(--text-primary)' }}>
                            Gastar hasta: {closeDateIso}
                          </strong>{' '}
                          · 🏦{' '}
                          <strong style={{ color: '#38bdf8' }}>
                            Pago: {dueDateIso}
                          </strong>
                        </span>
                        <span className={styles.itemMeta}>
                          Corte día {card.billingCloseDayOfMonth} · Pago día{' '}
                          {card.paymentDueDayOfMonth} ·{' '}
                          {typeof card.monthlyLimitCents === 'number' &&
                          card.monthlyLimitCents > 0
                            ? `Tope: ${formatSolesFromCents(
                                card.monthlyLimitCents
                              )}`
                            : `Tope global (${formatSolesFromCents(
                                snapshot.effectiveSpendingCapCents
                              )})`}
                        </span>

                        <div className={styles.itemActionsRow}>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() =>
                              isEditingCard
                                ? setEditingCardId(null)
                                : startEditingCreditCard(card)
                            }
                          >
                            {isEditingCard ? '✕ Cerrar' : '✏️ Editar'}
                          </button>
                          {onDeleteCreditCard && (
                            <button
                              type="button"
                              className={styles.dangerBtn}
                              onClick={() => onDeleteCreditCard(card.id)}
                            >
                              🗑️ Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {isEditingCard && (
                      <div className={styles.inlineEditBox}>
                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Alias</label>
                            <input
                              type="text"
                              value={editCardAlias}
                              onChange={(e) => setEditCardAlias(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Banco Emisor</label>
                            <input
                              type="text"
                              value={editCardBank}
                              onChange={(e) => setEditCardBank(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.fieldRowThree}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Día de Facturación (1–31)
                            </label>
                            <input
                              type="number"
                              min={1}
                              max={31}
                              value={editCardCloseDay}
                              onChange={(e) =>
                                setEditCardCloseDay(
                                  Math.min(
                                    31,
                                    Math.max(1, Number(e.target.value) || 20)
                                  )
                                )
                              }
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Día de Pago (1–31)
                            </label>
                            <input
                              type="number"
                              min={1}
                              max={31}
                              value={editCardDueDay}
                              onChange={(e) =>
                                setEditCardDueDay(
                                  Math.min(
                                    31,
                                    Math.max(1, Number(e.target.value) || 5)
                                  )
                                )
                              }
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Tope Tarjeta (S/)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Vacío = global"
                              value={editCardLimitSoles}
                              onChange={(e) =>
                                setEditCardLimitSoles(e.target.value)
                              }
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Últimos 4 dígitos
                            </label>
                            <input
                              type="text"
                              maxLength={4}
                              value={editCardLastFour}
                              onChange={(e) =>
                                setEditCardLastFour(
                                  e.target.value.replace(/\D/g, '').slice(0, 4)
                                )
                              }
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Color</label>
                            <select
                              value={editCardColor}
                              onChange={(e) => setEditCardColor(e.target.value)}
                              className={styles.select}
                            >
                              <option value="#10b981">🟢 Esmeralda</option>
                              <option value="#38bdf8">🔵 Celeste</option>
                              <option value="#f59e0b">🟠 Ámbar</option>
                              <option value="#a855f7">🟣 Violeta</option>
                              <option value="#ec4899">🌸 Rosa</option>
                            </select>
                          </div>
                        </div>

                        <div className={styles.itemActionsRow}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => handleSaveEditCreditCard(card.id)}
                          >
                            💾 Guardar Tarjeta
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => setEditingCardId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Sección Inferior: Ingresos No Recurrentes (Windfall Inflows) con Edición y Eliminación */}
      <div className={styles.windfallSection}>
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h3 className={styles.title}>3. Ingreso Extraordinario</h3>
            <InfoPopover title="Ingresos No Recurrentes">
              Bonos, ventas, gratificaciones o devoluciones: decide exactamente cómo impactan tu Límite de Gasto.
            </InfoPopover>
          </div>

          <form onSubmit={handleWindfallSubmit} className={styles.formStack}>
            <div className={styles.fieldGroup}>
              <label htmlFor="wf-desc" className={styles.label}>
                Descripción
              </label>
              <input
                id="wf-desc"
                type="text"
                placeholder="Ej. Bono extra, Venta de equipo, Freelance"
                value={wfDesc}
                onChange={(e) => setWfDesc(e.target.value)}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="wf-amount" className={styles.label}>
                  Monto Recibido (S/)
                </label>
                <input
                  id="wf-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Ej. 500.00"
                  value={wfAmountSoles}
                  onChange={(e) => setWfAmountSoles(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="wf-date" className={styles.label}>
                  Fecha de Recepción
                </label>
                <input
                  id="wf-date"
                  type="date"
                  value={wfDateIso}
                  onChange={(e) => setWfDateIso(e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label
                htmlFor="wf-mode"
                className={styles.label}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
              >
                Política de Afectación
                <InfoPopover title="Políticas de Ingreso Extraordinario">
                  Elige entre aplicar el {ratePercent}% del ciclo, definir un porcentaje exclusivo o destinar el 100% a reserva protegida ({formatSolesFromCents(previewWfSplit.capContributionCents)} al límite / {formatSolesFromCents(previewWfSplit.protectedSavingsCents)} a ahorro).
                </InfoPopover>
              </label>
              <select
                id="wf-mode"
                value={wfMode}
                onChange={(e) => setWfMode(e.target.value as WindfallAllocationMode)}
                className={styles.select}
              >
                <option value="USE_CYCLE_RATE">
                  1. % del ciclo actual ({ratePercent}%)
                </option>
                <option value="CUSTOM_RATE">
                  2. % personalizado
                </option>
                <option value="RESERVE_ONLY">
                  3. 100% Reserva / Ahorro (0% al límite)
                </option>
              </select>
            </div>

            {wfMode === 'CUSTOM_RATE' && (
              <div className={styles.fieldGroup}>
                <label htmlFor="wf-custom-pct" className={styles.label}>
                  % Personalizado ({wfCustomPercent}%)
                </label>
                <input
                  id="wf-custom-pct"
                  type="range"
                  min={1}
                  max={100}
                  step={1}
                  value={wfCustomPercent}
                  onChange={(e) => setWfCustomPercent(Number(e.target.value))}
                  className={styles.slider}
                />
              </div>
            )}

            <button type="submit" className={styles.primaryBtn}>
              + Agregar Ingreso Extraordinario
            </button>
          </form>
        </div>

        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h3 className={styles.title}>
              Extraordinarios en el Ciclo ({cycleState.windfallInflows.length})
            </h3>
            <InfoPopover title="Ingresos Extraordinarios Activos">
              Puedes editar el monto o cambiar la política de cualquier ingreso extraordinario en cualquier momento.
            </InfoPopover>
          </div>

          <div className={styles.list}>
            {cycleState.windfallInflows.length === 0 ? (
              <div className={styles.emptyStateBox}>
                No hay ingresos extraordinarios registrados en este ciclo.
              </div>
            ) : (
              cycleState.windfallInflows.map((wf) => {
                const isEditingWf = editingWfId === wf.id;
                const dynamicSplit = computeWindfallContributionCents(
                  wf.amountCents,
                  wf.allocationMode,
                  cycleState.spendingRateBp,
                  wf.customAllocationBp
                );
                return (
                  <div key={wf.id} className={styles.listItem}>
                    <div className={styles.listItemMain}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div className={styles.itemTitle}>{wf.description}</div>
                        <div>
                          <span className={styles.policyTag}>
                            {ALLOCATION_LABELS[wf.allocationMode]}
                            {wf.allocationMode === 'CUSTOM_RATE'
                              ? ` (${bpToPercent(wf.customAllocationBp)}%)`
                              : ''}
                          </span>
                        </div>
                        <span className={styles.itemMeta}>
                          Recibido el {wf.receivedAtIso.slice(0, 10)} · Aporta al límite:{' '}
                          <strong style={{ color: 'var(--accent-primary)' }}>
                            +{formatSolesFromCents(dynamicSplit.capContributionCents)}
                          </strong>{' '}
                          · Protegido en ahorro:{' '}
                          <strong style={{ color: 'var(--accent-info)' }}>
                            {formatSolesFromCents(dynamicSplit.protectedSavingsCents)}
                          </strong>
                        </span>
                        <div className={styles.itemActionsRow}>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() =>
                              isEditingWf
                                ? setEditingWfId(null)
                                : startEditingWindfall(wf)
                            }
                          >
                            {isEditingWf ? '✕ Cerrar Edición' : '✏️ Editar'}
                          </button>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            onClick={() => onDeleteWindfallInflow(wf.id)}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      </div>

                      <span className={styles.itemAmount}>
                        +{formatSolesFromCents(wf.amountCents)}
                      </span>
                    </div>

                    {isEditingWf && (
                      <div className={styles.inlineEditBox}>
                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Descripción</label>
                            <input
                              type="text"
                              value={editWfDesc}
                              onChange={(e) => setEditWfDesc(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Monto (S/)</label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={editWfAmountSoles}
                              onChange={(e) => setEditWfAmountSoles(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Política</label>
                            <select
                              value={editWfMode}
                              onChange={(e) =>
                                setEditWfMode(e.target.value as WindfallAllocationMode)
                              }
                              className={styles.select}
                            >
                              <option value="USE_CYCLE_RATE">
                                Aplicar % del ciclo ({ratePercent}%)
                              </option>
                              <option value="CUSTOM_RATE">% Personalizado</option>
                              <option value="RESERVE_ONLY">100% Reserva / Ahorro</option>
                            </select>
                          </div>

                          {editWfMode === 'CUSTOM_RATE' ? (
                            <div className={styles.fieldGroup}>
                              <label className={styles.label}>
                                % Personalizado ({editWfCustomPercent}%)
                              </label>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={editWfCustomPercent}
                                onChange={(e) =>
                                  setEditWfCustomPercent(Number(e.target.value) || 0)
                                }
                                className={styles.input}
                              />
                            </div>
                          ) : (
                            <div className={styles.fieldGroup}>
                              <label className={styles.label}>Fecha</label>
                              <input
                                type="date"
                                value={editWfDateIso}
                                onChange={(e) => setEditWfDateIso(e.target.value)}
                                className={styles.input}
                              />
                            </div>
                          )}
                        </div>

                        <div className={styles.itemActionsRow}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => handleSaveEditWindfall(wf.id)}
                          >
                            💾 Guardar Cambios
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => setEditingWfId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
